import React, { useContext, useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { AppContext } from '../context/AppContext'
import DashboardService from '../services/DashboardService'
import { Line, Bar, Doughnut, Pie } from 'react-chartjs-2'
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
} from 'chart.js'
import Swal from 'sweetalert2'

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement
)

const Dashboard = () => {
    const { currentUser } = useContext(AppContext)
    const [loading, setLoading] = useState(true)
    const [refreshing, setRefreshing] = useState(false)
    const [timeRange, setTimeRange] = useState('30days')
    const [dashboardData, setDashboardData] = useState({
        stats: {
            total_orders: 0,
            total_revenue: 0,
            total_customers: 0,
            total_products: 0,
            pending_orders: 0,
            processing_orders: 0,
            shipped_orders: 0,
            delivered_orders: 0,
            cancelled_orders: 0,
            low_stock_count: 0,
            out_of_stock_count: 0,
            new_customers_today: 0,
            revenue_today: 0,
            orders_today: 0,
            average_order_value: 0,
            top_selling_category: '',
            conversion_rate: 0
        },
        recentOrders: [],
        topProducts: [],
        lowStockProducts: [],
        recentCustomers: [],
        salesChart: { labels: [], data: [] },
        revenueChart: { labels: [], data: [] },
        orderStatusDistribution: { labels: [], data: [] },
        customerGrowth: { labels: [], data: [] },
        categoryPerformance: { labels: [], data: [] },
        dailyMetrics: { labels: [], orders: [], revenue: [], customers: [] }
    })

    const [autoRefresh, setAutoRefresh] = useState(true)

    useEffect(() => {
        fetchDashboardData()
        
        // Auto refresh every 5 minutes if enabled
        const interval = autoRefresh ? setInterval(() => {
            fetchDashboardData(true)
        }, 300000) : null

        return () => {
            if (interval) clearInterval(interval)
        }
    }, [autoRefresh, timeRange])

    const fetchDashboardData = useCallback(async (isAutoRefresh = false) => {
        if (!isAutoRefresh) {
            setLoading(true)
        } else {
            setRefreshing(true)
        }

        try {
            // Fetch all dashboard data in parallel
            const [
                statsResponse,
                recentOrdersResponse,
                topProductsResponse,
                lowStockResponse,
                recentCustomersResponse,
                salesChartResponse,
                revenueResponse,
                orderStatusResponse,
                customerGrowthResponse,
                categoryPerformanceResponse,
                dailyMetricsResponse
            ] = await Promise.allSettled([
                DashboardService.getDashboardStats(),
                DashboardService.getRecentOrders(10),
                DashboardService.getTopProducts(8),
                DashboardService.getLowStockProducts(10),
                DashboardService.getRecentCustomers(5),
                DashboardService.getSalesChartData(timeRange),
                DashboardService.getRevenueData(timeRange),
                DashboardService.getOrderStatusDistribution(),
                DashboardService.getCustomerGrowth(timeRange),
                DashboardService.getCategoryPerformance(),
                DashboardService.getDailyMetrics(timeRange)
            ])

            // Handle successful responses
            const newDashboardData = { ...dashboardData }

            if (statsResponse.status === 'fulfilled' && statsResponse.value.success) {
                newDashboardData.stats = { ...newDashboardData.stats, ...statsResponse.value.data }
            }

            if (recentOrdersResponse.status === 'fulfilled' && recentOrdersResponse.value.success) {
                newDashboardData.recentOrders = recentOrdersResponse.value.data
            }

            if (topProductsResponse.status === 'fulfilled' && topProductsResponse.value.success) {
                newDashboardData.topProducts = topProductsResponse.value.data
            }

            if (lowStockResponse.status === 'fulfilled' && lowStockResponse.value.success) {
                newDashboardData.lowStockProducts = lowStockResponse.value.data
            }

            if (recentCustomersResponse.status === 'fulfilled' && recentCustomersResponse.value.success) {
                newDashboardData.recentCustomers = recentCustomersResponse.value.data
            }

            if (salesChartResponse.status === 'fulfilled' && salesChartResponse.value.success) {
                newDashboardData.salesChart = salesChartResponse.value.data
            }

            if (revenueResponse.status === 'fulfilled' && revenueResponse.value.success) {
                newDashboardData.revenueChart = revenueResponse.value.data
            }

            if (orderStatusResponse.status === 'fulfilled' && orderStatusResponse.value.success) {
                newDashboardData.orderStatusDistribution = orderStatusResponse.value.data
            }

            if (customerGrowthResponse.status === 'fulfilled' && customerGrowthResponse.value.success) {
                newDashboardData.customerGrowth = customerGrowthResponse.value.data
            }

            if (categoryPerformanceResponse.status === 'fulfilled' && categoryPerformanceResponse.value.success) {
                newDashboardData.categoryPerformance = categoryPerformanceResponse.value.data
            }

            if (dailyMetricsResponse.status === 'fulfilled' && dailyMetricsResponse.value.success) {
                newDashboardData.dailyMetrics = dailyMetricsResponse.value.data
            }

            setDashboardData(newDashboardData)

            if (isAutoRefresh) {
                // Show success toast for auto refresh
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: 'Dashboard updated',
                    showConfirmButton: false,
                    timer: 2000
                })
            }

        } catch (error) {
            console.error('Error fetching dashboard data:', error)
            if (!isAutoRefresh) {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to load dashboard data. Please try again.',
                })
            }
        } finally {
            setLoading(false)
            setRefreshing(false)
        }
    }, [timeRange, dashboardData])

    // Chart configurations
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                grid: {
                    color: '#f8f9fa',
                },
            },
            x: {
                grid: {
                    color: '#f8f9fa',
                },
            },
        },
    }

    const salesChartConfig = {
        labels: dashboardData.salesChart.labels,
        datasets: [
            {
                label: 'Sales',
                data: dashboardData.salesChart.data,
                borderColor: '#007bff',
                backgroundColor: 'rgba(0, 123, 255, 0.1)',
                tension: 0.4,
                fill: true,
            },
        ],
    }

    const revenueChartConfig = {
        labels: dashboardData.revenueChart.labels,
        datasets: [
            {
                label: 'Revenue ($)',
                data: dashboardData.revenueChart.data,
                backgroundColor: '#28a745',
                borderColor: '#28a745',
                borderWidth: 2,
            },
        ],
    }

    const orderStatusChartConfig = {
        labels: dashboardData.orderStatusDistribution.labels,
        datasets: [
            {
                data: dashboardData.orderStatusDistribution.data,
                backgroundColor: [
                    '#ffc107', // Pending
                    '#17a2b8', // Processing  
                    '#007bff', // Shipped
                    '#28a745', // Delivered
                    '#dc3545', // Cancelled
                    '#6c757d', // Returned
                ],
                borderWidth: 2,
                borderColor: '#fff',
            },
        ],
    }

    const customerGrowthConfig = {
        labels: dashboardData.customerGrowth.labels,
        datasets: [
            {
                label: 'New Customers',
                data: dashboardData.customerGrowth.data,
                borderColor: '#17a2b8',
                backgroundColor: 'rgba(23, 162, 184, 0.1)',
                tension: 0.4,
                fill: true,
            },
        ],
    }

    const categoryPerformanceConfig = {
        labels: dashboardData.categoryPerformance.labels,
        datasets: [
            {
                label: 'Sales by Category',
                data: dashboardData.categoryPerformance.data,
                backgroundColor: [
                    '#007bff',
                    '#28a745', 
                    '#ffc107',
                    '#dc3545',
                    '#17a2b8',
                    '#6f42c1',
                    '#fd7e14',
                    '#20c997'
                ],
                borderWidth: 2,
                borderColor: '#fff',
            },
        ],
    }

    const dailyMetricsConfig = {
        labels: dashboardData.dailyMetrics.labels,
        datasets: [
            {
                label: 'Orders',
                data: dashboardData.dailyMetrics.orders,
                borderColor: '#007bff',
                backgroundColor: 'rgba(0, 123, 255, 0.1)',
                yAxisID: 'y',
            },
            {
                label: 'Revenue ($)',
                data: dashboardData.dailyMetrics.revenue,
                borderColor: '#28a745',
                backgroundColor: 'rgba(40, 167, 69, 0.1)',
                yAxisID: 'y1',
            },
        ],
    }

    const multiAxisOptions = {
        ...chartOptions,
        interaction: {
            mode: 'index',
            intersect: false,
        },
        scales: {
            x: {
                display: true,
                title: {
                    display: true,
                    text: 'Date'
                }
            },
            y: {
                type: 'linear',
                display: true,
                position: 'left',
                title: {
                    display: true,
                    text: 'Orders'
                }
            },
            y1: {
                type: 'linear',
                display: true,
                position: 'right',
                title: {
                    display: true,
                    text: 'Revenue ($)'
                },
                grid: {
                    drawOnChartArea: false,
                },
            },
        },
    }

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        }).format(amount)
    }

    const formatNumber = (number) => {
        return new Intl.NumberFormat('en-US').format(number)
    }

    const formatPercentage = (number) => {
        return `${parseFloat(number).toFixed(1)}%`
    }

    const getStatusBadge = (status) => {
        const statusClasses = {
            'pending': 'badge-warning',
            'processing': 'badge-info',
            'shipped': 'badge-primary',
            'delivered': 'badge-success',
            'cancelled': 'badge-danger',
            'returned': 'badge-secondary'
        }
        return statusClasses[status?.toLowerCase()] || 'badge-secondary'
    }

    const getGrowthIcon = (current, previous) => {
        if (current > previous) return <i className="fas fa-arrow-up text-success"></i>
        if (current < previous) return <i className="fas fa-arrow-down text-danger"></i>
        return <i className="fas fa-minus text-muted"></i>
    }

    const calculateGrowthPercentage = (current, previous) => {
        if (!previous) return 0
        return ((current - previous) / previous * 100).toFixed(1)
    }

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center dashboard-loading">
                <div className="text-center">
                    <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <h5 className="text-muted">Loading Dashboard...</h5>
                </div>
            </div>
        )
    }

    return (
        <div className="dashboard-container">
            {/* Page Header */}
            <div className="dashboard-header">
                <div className="row align-items-center">
                    <div className="col-md-6">
                        <h1 className="h3 mb-0">
                            <i className="fas fa-tachometer-alt me-2 text-primary"></i>
                            Dashboard
                        </h1>
                        <small className="text-muted">
                            Welcome back, <strong>{currentUser?.name || 'Admin'}</strong>
                        </small>
                    </div>
                    <div className="col-md-6 text-md-end mt-3 mt-md-0">
                        <div className="d-flex flex-wrap justify-content-md-end gap-2">
                            {/* Time Range Selector */}
                            <select 
                                className="form-select form-select-sm"
                                value={timeRange}
                                onChange={(e) => setTimeRange(e.target.value)}
                                style={{ width: 'auto' }}
                            >
                                <option value="7days">Last 7 Days</option>
                                <option value="30days">Last 30 Days</option>
                                <option value="3months">Last 3 Months</option>
                                <option value="6months">Last 6 Months</option>
                                <option value="12months">Last 12 Months</option>
                            </select>

                            {/* Auto Refresh Toggle */}
                            <div className="form-check form-switch">
                                <input 
                                    className="form-check-input" 
                                    type="checkbox" 
                                    id="autoRefresh"
                                    checked={autoRefresh}
                                    onChange={(e) => setAutoRefresh(e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor="autoRefresh">
                                    Auto-refresh
                                </label>
                            </div>

                            {/* Refresh Button */}
                            <button 
                                className="btn btn-primary btn-sm"
                                onClick={() => fetchDashboardData(false)}
                                disabled={loading || refreshing}
                            >
                                <i className={`fas fa-sync-alt me-1 ${refreshing ? 'fa-spin' : ''}`}></i>
                                Refresh
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Key Metrics Cards */}
            <div className="row g-3 mb-4">
                {/* Today's Metrics */}
                <div className="col-xl-3 col-lg-6 col-md-6">
                    <div className="card metric-card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <div className="d-flex align-items-center justify-content-between">
                                <div>
                                    <div className="metric-icon bg-primary bg-opacity-10 text-primary">
                                        <i className="fas fa-calendar-day"></i>
                                    </div>
                                </div>
                                <div className="text-end">
                                    <h6 className="text-muted mb-1">Today's Orders</h6>
                                    <h3 className="mb-0">{formatNumber(dashboardData.stats.orders_today)}</h3>
                                    <small className="text-success">
                                        <i className="fas fa-arrow-up"></i> Active
                                    </small>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-xl-3 col-lg-6 col-md-6">
                    <div className="card metric-card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <div className="d-flex align-items-center justify-content-between">
                                <div>
                                    <div className="metric-icon bg-success bg-opacity-10 text-success">
                                        <i className="fas fa-dollar-sign"></i>
                                    </div>
                                </div>
                                <div className="text-end">
                                    <h6 className="text-muted mb-1">Today's Revenue</h6>
                                    <h3 className="mb-0">{formatCurrency(dashboardData.stats.revenue_today)}</h3>
                                    <small className="text-success">
                                        <i className="fas fa-arrow-up"></i> +12.5%
                                    </small>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-xl-3 col-lg-6 col-md-6">
                    <div className="card metric-card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <div className="d-flex align-items-center justify-content-between">
                                <div>
                                    <div className="metric-icon bg-info bg-opacity-10 text-info">
                                        <i className="fas fa-user-plus"></i>
                                    </div>
                                </div>
                                <div className="text-end">
                                    <h6 className="text-muted mb-1">New Customers</h6>
                                    <h3 className="mb-0">{formatNumber(dashboardData.stats.new_customers_today)}</h3>
                                    <small className="text-info">
                                        <i className="fas fa-arrow-up"></i> Today
                                    </small>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-xl-3 col-lg-6 col-md-6">
                    <div className="card metric-card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <div className="d-flex align-items-center justify-content-between">
                                <div>
                                    <div className="metric-icon bg-warning bg-opacity-10 text-warning">
                                        <i className="fas fa-chart-line"></i>
                                    </div>
                                </div>
                                <div className="text-end">
                                    <h6 className="text-muted mb-1">Conversion Rate</h6>
                                    <h3 className="mb-0">{formatPercentage(dashboardData.stats.conversion_rate)}</h3>
                                    <small className="text-warning">
                                        <i className="fas fa-chart-line"></i> Rate
                                    </small>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Statistics Cards */}
            <div className="row g-3 mb-4">
                <div className="col-xl-2 col-lg-4 col-md-6 col-sm-6">
                    <div className="card stat-card border-0 shadow-sm h-100">
                        <div className="card-body text-center">
                            <div className="stat-icon bg-primary bg-opacity-10 text-primary mx-auto mb-3">
                                <i className="fas fa-shopping-cart"></i>
                            </div>
                            <h6 className="card-title text-muted mb-1">Total Orders</h6>
                            <h4 className="mb-2">{formatNumber(dashboardData.stats.total_orders)}</h4>
                            <div className="progress" style={{ height: '4px' }}>
                                <div className="progress-bar bg-primary" style={{ width: '85%' }}></div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-xl-2 col-lg-4 col-md-6 col-sm-6">
                    <div className="card stat-card border-0 shadow-sm h-100">
                        <div className="card-body text-center">
                            <div className="stat-icon bg-success bg-opacity-10 text-success mx-auto mb-3">
                                <i className="fas fa-dollar-sign"></i>
                            </div>
                            <h6 className="card-title text-muted mb-1">Total Revenue</h6>
                            <h4 className="mb-2">{formatCurrency(dashboardData.stats.total_revenue)}</h4>
                            <div className="progress" style={{ height: '4px' }}>
                                <div className="progress-bar bg-success" style={{ width: '92%' }}></div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-xl-2 col-lg-4 col-md-6 col-sm-6">
                    <div className="card stat-card border-0 shadow-sm h-100">
                        <div className="card-body text-center">
                            <div className="stat-icon bg-info bg-opacity-10 text-info mx-auto mb-3">
                                <i className="fas fa-users"></i>
                            </div>
                            <h6 className="card-title text-muted mb-1">Total Customers</h6>
                            <h4 className="mb-2">{formatNumber(dashboardData.stats.total_customers)}</h4>
                            <div className="progress" style={{ height: '4px' }}>
                                <div className="progress-bar bg-info" style={{ width: '78%' }}></div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-xl-2 col-lg-4 col-md-6 col-sm-6">
                    <div className="card stat-card border-0 shadow-sm h-100">
                        <div className="card-body text-center">
                            <div className="stat-icon bg-warning bg-opacity-10 text-warning mx-auto mb-3">
                                <i className="fas fa-box"></i>
                            </div>
                            <h6 className="card-title text-muted mb-1">Total Products</h6>
                            <h4 className="mb-2">{formatNumber(dashboardData.stats.total_products)}</h4>
                            <div className="progress" style={{ height: '4px' }}>
                                <div className="progress-bar bg-warning" style={{ width: '65%' }}></div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-xl-2 col-lg-4 col-md-6 col-sm-6">
                    <div className="card stat-card border-0 shadow-sm h-100">
                        <div className="card-body text-center">
                            <div className="stat-icon bg-orange bg-opacity-10 text-orange mx-auto mb-3">
                                <i className="fas fa-clock"></i>
                            </div>
                            <h6 className="card-title text-muted mb-1">Pending Orders</h6>
                            <h4 className="mb-2">{formatNumber(dashboardData.stats.pending_orders)}</h4>
                            <div className="progress" style={{ height: '4px' }}>
                                <div className="progress-bar bg-orange" style={{ width: '45%' }}></div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-xl-2 col-lg-4 col-md-6 col-sm-6">
                    <div className="card stat-card border-0 shadow-sm h-100">
                        <div className="card-body text-center">
                            <div className="stat-icon bg-danger bg-opacity-10 text-danger mx-auto mb-3">
                                <i className="fas fa-exclamation-triangle"></i>
                            </div>
                            <h6 className="card-title text-muted mb-1">Low Stock</h6>
                            <h4 className="mb-2">{formatNumber(dashboardData.stats.low_stock_count)}</h4>
                            <div className="progress" style={{ height: '4px' }}>
                                <div className="progress-bar bg-danger" style={{ width: '30%' }}></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Charts Row 1 */}
            <div className="row g-3 mb-4">
                <div className="col-xl-8 col-lg-7">
                    <div className="card border-0 shadow-sm">
                        <div className="card-header bg-white border-0 d-flex justify-content-between align-items-center">
                            <h6 className="card-title mb-0">
                                <i className="fas fa-chart-line text-primary me-2"></i>
                                Sales & Revenue Trends
                            </h6>
                            <div className="btn-group btn-group-sm" role="group">
                                <input type="radio" className="btn-check" name="chartType" id="salesChart" defaultChecked />
                                <label className="btn btn-outline-primary" htmlFor="salesChart">Sales</label>
                                <input type="radio" className="btn-check" name="chartType" id="revenueChart" />
                                <label className="btn btn-outline-primary" htmlFor="revenueChart">Revenue</label>
                            </div>
                        </div>
                        <div className="card-body">
                            <div className="chart-container">
                                <Line data={dailyMetricsConfig} options={multiAxisOptions} />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-xl-4 col-lg-5">
                    <div className="card border-0 shadow-sm">
                        <div className="card-header bg-white border-0">
                            <h6 className="card-title mb-0">
                                <i className="fas fa-chart-pie text-info me-2"></i>
                                Order Status Distribution
                            </h6>
                        </div>
                        <div className="card-body">
                            <div className="chart-container">
                                <Doughnut data={orderStatusChartConfig} options={{ maintainAspectRatio: false }} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Charts Row 2 */}
            <div className="row g-3 mb-4">
                <div className="col-xl-6 col-lg-6">
                    <div className="card border-0 shadow-sm">
                        <div className="card-header bg-white border-0">
                            <h6 className="card-title mb-0">
                                <i className="fas fa-users text-success me-2"></i>
                                Customer Growth
                            </h6>
                        </div>
                        <div className="card-body">
                            <div className="chart-container">
                                <Line data={customerGrowthConfig} options={chartOptions} />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-xl-6 col-lg-6">
                    <div className="card border-0 shadow-sm">
                        <div className="card-header bg-white border-0">
                            <h6 className="card-title mb-0">
                                <i className="fas fa-tags text-warning me-2"></i>
                                Category Performance
                            </h6>
                        </div>
                        <div className="card-body">
                            <div className="chart-container">
                                <Pie data={categoryPerformanceConfig} options={{ maintainAspectRatio: false }} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Data Tables Row */}
            <div className="row g-3 mb-4">
                {/* Recent Orders */}
                <div className="col-xl-6 col-lg-12">
                    <div className="card border-0 shadow-sm">
                        <div className="card-header bg-white border-0 d-flex justify-content-between align-items-center">
                            <h6 className="card-title mb-0">
                                <i className="fas fa-shopping-bag text-primary me-2"></i>
                                Recent Orders
                            </h6>
                            <Link to="/orders" className="btn btn-sm btn-outline-primary">
                                <i className="fas fa-external-link-alt me-1"></i>
                                View All
                            </Link>
                        </div>
                        <div className="card-body p-0">
                            <div className="table-responsive">
                                <table className="table table-hover mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Order ID</th>
                                            <th>Customer</th>
                                            <th>Amount</th>
                                            <th>Status</th>
                                            <th className="d-none d-md-table-cell">Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {dashboardData.recentOrders.length > 0 ? (
                                            dashboardData.recentOrders.slice(0, 8).map((order) => (
                                                <tr key={order.order_id}>
                                                    <td>
                                                        <Link to={`/orders/${order.order_id}`} className="text-decoration-none fw-medium">
                                                            #{order.order_id}
                                                        </Link>
                                                    </td>
                                                    <td>
                                                        <div>
                                                            <div className="fw-medium">{order.customer_name}</div>
                                                            <small className="text-muted d-md-none">
                                                                {new Date(order.created_at).toLocaleDateString()}
                                                            </small>
                                                        </div>
                                                    </td>
                                                    <td className="fw-medium">{formatCurrency(order.total_amount)}</td>
                                                    <td>
                                                        <span className={`badge ${getStatusBadge(order.status)}`}>
                                                            {order.status}
                                                        </span>
                                                    </td>
                                                    <td className="d-none d-md-table-cell text-muted">
                                                        {new Date(order.created_at).toLocaleDateString()}
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="5" className="text-center py-4 text-muted">
                                                    <i className="fas fa-shopping-cart fa-2x mb-2 d-block"></i>
                                                    No recent orders found
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Top Products */}
                <div className="col-xl-6 col-lg-12">
                    <div className="card border-0 shadow-sm">
                        <div className="card-header bg-white border-0 d-flex justify-content-between align-items-center">
                            <h6 className="card-title mb-0">
                                <i className="fas fa-star text-warning me-2"></i>
                                Top Selling Products
                            </h6>
                            <Link to="/products" className="btn btn-sm btn-outline-primary">
                                <i className="fas fa-external-link-alt me-1"></i>
                                View All
                            </Link>
                        </div>
                        <div className="card-body p-0">
                            <div className="table-responsive">
                                <table className="table table-hover mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Product</th>
                                            <th className="text-center">Sales</th>
                                            <th className="text-end">Revenue</th>
                                            <th className="text-center">Stock</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {dashboardData.topProducts.length > 0 ? (
                                            dashboardData.topProducts.slice(0, 6).map((product, index) => (
                                                <tr key={product.product_id}>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <div className="position-relative me-3">
                                                                {product.thumb_file_name ? (
                                                                    <img 
                                                                        src={`${process.env.REACT_APP_IMAGE_STORAGE}uploads/products/${product.product_id}/${product.thumb_file_name}`}
                                                                        alt={product.name}
                                                                        className="rounded"
                                                                        style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                                                                        onError={(e) => {
                                                                            e.target.src = '/images/no-image.png';
                                                                            e.target.onerror = null;
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <div className="bg-light rounded d-flex align-items-center justify-content-center" 
                                                                         style={{ width: '40px', height: '40px' }}>
                                                                        <i className="fas fa-box text-muted"></i>
                                                                    </div>
                                                                )}
                                                                <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-primary" 
                                                                      style={{ fontSize: '0.6em' }}>
                                                                    {index + 1}
                                                                </span>
                                                            </div>
                                                            <div>
                                                                <div className="fw-medium text-truncate" style={{ maxWidth: '150px' }}>
                                                                    {product.name}
                                                                </div>
                                                                <small className="text-muted">SKU: {product.sku}</small>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="text-center fw-medium">{formatNumber(product.total_sales)}</td>
                                                    <td className="text-end fw-medium">{formatCurrency(product.total_revenue)}</td>
                                                    <td className="text-center">
                                                        <span className={`badge ${product.stock < 10 ? 'bg-danger' : product.stock < 50 ? 'bg-warning' : 'bg-success'}`}>
                                                            {product.stock}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="4" className="text-center py-4 text-muted">
                                                    <i className="fas fa-box fa-2x mb-2 d-block"></i>
                                                    No product data found
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Recent Customers & Low Stock Alerts */}
            <div className="row g-3 mb-4">
                {/* Recent Customers */}
                <div className="col-xl-6 col-lg-12">
                    <div className="card border-0 shadow-sm">
                        <div className="card-header bg-white border-0 d-flex justify-content-between align-items-center">
                            <h6 className="card-title mb-0">
                                <i className="fas fa-user-plus text-success me-2"></i>
                                New Customers
                            </h6>
                            <Link to="/customers" className="btn btn-sm btn-outline-primary">
                                <i className="fas fa-external-link-alt me-1"></i>
                                View All
                            </Link>
                        </div>
                        <div className="card-body">
                            {dashboardData.recentCustomers.length > 0 ? (
                                <div className="row g-2">
                                    {dashboardData.recentCustomers.map((customer) => (
                                        <div key={customer.customer_id} className="col-12">
                                            <div className="d-flex align-items-center p-2 bg-light rounded">
                                                <div className="avatar-circle bg-success text-white me-3">
                                                    {customer.name?.charAt(0)?.toUpperCase() || 'C'}
                                                </div>
                                                <div className="flex-grow-1">
                                                    <div className="fw-medium">{customer.name}</div>
                                                    <small className="text-muted">{customer.email}</small>
                                                </div>
                                                <div className="text-end">
                                                    <small className="text-muted">
                                                        {new Date(customer.created_at).toLocaleDateString()}
                                                    </small>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-4 text-muted">
                                    <i className="fas fa-users fa-2x mb-2 d-block"></i>
                                    No new customers found
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Low Stock Products */}
                <div className="col-xl-6 col-lg-12">
                    {dashboardData.lowStockProducts.length > 0 ? (
                        <div className="card border-0 shadow-sm border-start border-danger border-4">
                            <div className="card-header bg-danger bg-opacity-10 border-0 d-flex justify-content-between align-items-center">
                                <h6 className="card-title mb-0 text-danger">
                                    <i className="fas fa-exclamation-triangle me-2"></i>
                                    Low Stock Alert ({dashboardData.lowStockProducts.length})
                                </h6>
                                <Link to="/products?filter=low-stock" className="btn btn-sm btn-outline-danger">
                                    <i className="fas fa-cog me-1"></i>
                                    Manage Stock
                                </Link>
                            </div>
                            <div className="card-body">
                                <div className="row g-2">
                                    {dashboardData.lowStockProducts.slice(0, 6).map((product) => (
                                        <div key={product.product_id} className="col-12">
                                            <div className="alert alert-danger py-2 mb-2">
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div className="d-flex align-items-center">
                                                        {product.thumb_file_name && (
                                                            <img 
                                                                src={`${process.env.REACT_APP_IMAGE_STORAGE}uploads/products/${product.product_id}/${product.thumb_file_name}`}
                                                                alt={product.name}
                                                                className="rounded me-2"
                                                                style={{ width: '30px', height: '30px', objectFit: 'cover' }}
                                                                onError={(e) => {
                                                                    e.target.src = '/images/no-image.png';
                                                                    e.target.onerror = null;
                                                                }}
                                                            />
                                                        )}
                                                        <div>
                                                            <strong className="text-truncate d-block" style={{ maxWidth: '180px' }}>
                                                                {product.name}
                                                            </strong>
                                                            <small>SKU: {product.sku}</small>
                                                        </div>
                                                    </div>
                                                    <span className="badge bg-danger fs-6">
                                                        {product.stock} left
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="card border-0 shadow-sm">
                            <div className="card-header bg-success bg-opacity-10 border-0">
                                <h6 className="card-title mb-0 text-success">
                                    <i className="fas fa-check-circle me-2"></i>
                                    Stock Status
                                </h6>
                            </div>
                            <div className="card-body text-center">
                                <i className="fas fa-check-circle fa-3x text-success mb-3"></i>
                                <h6 className="text-success">All Products in Stock</h6>
                                <p className="text-muted mb-0">No low stock alerts at the moment</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Custom CSS */}
            <style jsx>{`
                .dashboard-container {
                    padding: 1.5rem;
                    background-color: #f8f9fa;
                    min-height: 100vh;
                }

                .dashboard-header {
                    background: white;
                    padding: 1.5rem;
                    border-radius: 10px;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                    margin-bottom: 1.5rem;
                }

                .dashboard-loading {
                    min-height: 70vh;
                    background-color: #f8f9fa;
                }

                .metric-card, .stat-card {
                    transition: all 0.3s ease;
                    border: 1px solid rgba(0,0,0,0.05) !important;
                }

                .metric-card:hover, .stat-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 8px 25px rgba(0,0,0,0.15) !important;
                }

                .metric-icon, .stat-icon {
                    width: 60px;
                    height: 60px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 24px;
                    border-radius: 50%;
                }

                .card {
                    transition: all 0.3s ease;
                    border: none;
                    border-radius: 12px;
                }

                .card:hover {
                    box-shadow: 0 8px 25px rgba(0,0,0,0.1) !important;
                }

                .card-header {
                    border-radius: 12px 12px 0 0 !important;
                    padding: 1.25rem 1.5rem;
                }

                .card-body {
                    padding: 1.5rem;
                }

                .chart-container {
                    height: 300px;
                    position: relative;
                }

                .table th {
                    font-weight: 600;
                    font-size: 0.875rem;
                    padding: 1rem 1.25rem;
                    border-bottom: 2px solid #dee2e6;
                }

                .table td {
                    padding: 1rem 1.25rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f3f4;
                }

                .table tbody tr:hover {
                    background-color: #f8f9fa;
                }

                .avatar-circle {
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: bold;
                    font-size: 16px;
                }

                .badge {
                    font-size: 0.75em;
                    padding: 0.5em 0.75em;
                }

                .progress {
                    background-color: rgba(0,0,0,0.1);
                }

                .bg-orange {
                    background-color: #fd7e14 !important;
                }

                .text-orange {
                    color: #fd7e14 !important;
                }

                .border-start {
                    border-left: 4px solid !important;
                }

                /* Mobile Responsive */
                @media (max-width: 768px) {
                    .dashboard-container {
                        padding: 1rem;
                    }
                    
                    .dashboard-header {
                        padding: 1rem;
                        margin-bottom: 1rem;
                    }
                    
                    .card-body {
                        padding: 1rem;
                    }
                    
                    .metric-icon, .stat-icon {
                        width: 48px;
                        height: 48px;
                        font-size: 20px;
                    }
                    
                    .chart-container {
                        height: 250px;
                    }
                    
                    .table-responsive {
                        font-size: 0.875rem;
                    }
                    
                    .table th, .table td {
                        padding: 0.75rem 0.5rem;
                    }
                    
                    .d-flex.gap-2 {
                        flex-direction: column;
                        gap: 0.5rem !important;
                    }
                    
                    .btn-group-sm .btn {
                        font-size: 0.75rem;
                        padding: 0.25rem 0.5rem;
                    }
                }

                @media (max-width: 576px) {
                    .metric-icon, .stat-icon {
                        width: 40px;
                        height: 40px;
                        font-size: 16px;
                    }
                    
                    .metric-card h3, .stat-card h4 {
                        font-size: 1.25rem;
                    }
                    
                    .card-header h6 {
                        font-size: 0.875rem;
                    }
                    
                    .chart-container {
                        height: 200px;
                    }
                    
                    .text-truncate {
                        max-width: 120px !important;
                    }
                }

                /* Animation classes */
                .fade-in {
                    animation: fadeIn 0.5s ease-in;
                }

                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                .slide-up {
                    animation: slideUp 0.3s ease-out;
                }

                @keyframes slideUp {
                    from { transform: translateY(10px); opacity: 0; }
                    to { transform: translateY(0); opacity: 1; }
                }

                /* Custom scrollbar */
                .table-responsive::-webkit-scrollbar {
                    height: 6px;
                }

                .table-responsive::-webkit-scrollbar-track {
                    background: #f1f1f1;
                    border-radius: 3px;
                }

                .table-responsive::-webkit-scrollbar-thumb {
                    background: #c1c1c1;
                    border-radius: 3px;
                }

                .table-responsive::-webkit-scrollbar-thumb:hover {
                    background: #a8a8a8;
                }
            `}</style>
        </div>
    )
}

export default Dashboard
